import { useOffersList, type OfferItem } from '@/lib/api/offers';
import React, { createContext, useContext } from 'react';
import { useParams } from 'react-router-dom';

interface OffersContextValue {
  offers: OfferItem[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const OffersContext = createContext<OffersContextValue | undefined>(undefined);

interface OffersProviderProps {
  children: React.ReactNode;
  storeId?: string;
}

export const OffersProvider: React.FC<OffersProviderProps> = ({ children, storeId: storeIdProp }) => {
  const { id } = useParams<{ id: string }>();
  const storeId = storeIdProp || id;

  if (!storeId) {
    throw new Error('Store id is not defined');
  }

  // Refetch offers when provider mounts to ensure fresh data
  const {
    data,
    isLoading,
    error: queryError,
    refetch: refetchQuery,
  } = useOffersList(
    {
      storeId,
    },
    {
      refetchOnMount: 'always', // Always refetch when mounted
      refetchOnWindowFocus: false, // Don't refetch on window focus
    },
  );

  const offers = data?.offers ?? [];
  const loading = isLoading;
  const error = queryError ? new Error(queryError.message) : null;

  const refetch = async () => {
    await refetchQuery();
  };

  const value: OffersContextValue = {
    offers,
    loading,
    error,
    refetch,
  };

  return <OffersContext.Provider value={value}>{children}</OffersContext.Provider>;
};

export const useOffersContext = () => {
  const context = useContext(OffersContext);

  if (!context) {
    throw new Error('useOffersContext must be used within an OffersProvider');
  }

  return context;
};

/**
 * Safe version that returns null instead of throwing
 * Use this when the provider might not be available
 */
export const useOffersContextSafe = () => {
  const context = useContext(OffersContext);
  return context || null;
};
